/**
 * STUDIO PROJECTS domain state — named working sets the Studio opens from.
 *
 * Modelled on playlistsStore, deliberately and closely: a flat `create<State>` with
 * single-domain mutations, a fire-and-forget `void studioProjectsApi.x(...)` after each
 * one, `hydrate`/`reset`, and pure selector *functions* exported at the bottom rather
 * than selector methods on the state. Anything spanning domains — resolving a project's
 * songs, toasting, opening the workspace — is a hook (hooks/useStudioProjects.ts), which
 * is the division of labour stores/README.md states.
 *
 * WHAT A PROJECT HOLDS. Ids, not songs. See the note on the `StudioProject` type. Every
 * read resolves ids against songsStore, so nothing here has to be kept in step with the
 * library — and there is NO SECOND MUSIC STORE anywhere in this feature, which Batch 6 §3
 * rules out by name. A project is a name and a set of references.
 *
 * NOT PERSISTED, and the screens say so rather than implying otherwise. Projects live in
 * memory for the session, exactly as playlists, songs, credits and the player do, because
 * studioProjectsApi is a mock — see its header. §3 asks specifically that persistence not
 * be pretended, so routes/StudioEntry.tsx carries the note on screen.
 *
 * WHY `activeProjectId` IS HERE rather than in studioStore: studioStore's `songId` is
 * synced to the route param and cleared when the workspace unmounts, so it is scoped to
 * one visit. Which project you are working in outlives that — you switch tracks inside a
 * project — so it belongs with the projects. It is the one piece of state the workspace
 * reads from this store, and all it does there is scope the MUSIC LIBRARY strip and the
 * transport's Prev/Next to the project's tracks. See routes/Studio.tsx.
 */
import { create } from 'zustand';
import { studioProjectsApi } from '../api/studioProjectsApi';
import { newId } from '../lib/ids';
import type { ProjectSongScope, Song, StudioProject } from '../types/domain';
import { IDLE_LOAD, LOADING_LOAD, READY_LOAD, failedLoad, type LoadState } from './loadState';

/** Display date for a project created in this session. Matches songsStore's copy. */
const NOW_LABEL = 'Just now';

interface StudioProjectsState {
  projects: StudioProject[];
  /** Monotonic ordering counter. Higher = newer. */
  seq: number;
  /**
   * The project the Studio is currently working in, or null.
   *
   * Null is a real state and not a gap: the workspace is reachable directly from the
   * Library (a song row opens it), and arriving that way is not working in a project.
   * Nothing is invented to fill it — see routes/Studio.tsx, which falls back to the
   * whole library when there is no project.
   */
  activeProjectId: string | null;
  /**
   * Where the project READ has got to. See stores/loadState.ts.
   *
   * The same reason playlistsStore needs one: this store starts empty and its empty state
   * is the designed "create your first project" screen, so `projects.length === 0` cannot
   * be the condition for showing it — that renders the onboarding screen over a fetch
   * about to return the user's projects.
   */
  load: LoadState;

  /** Creates a project and returns it, so the caller can open it. */
  createProject: (name: string, songIds: string[], scope: ProjectSongScope) => StudioProject;
  renameProject: (id: string, name: string) => void;
  deleteProject: (id: string) => void;
  /** Appends a song. A no-op if the project already contains it. */
  addSongToProject: (projectId: string, songId: string) => void;
  removeSongFromProject: (projectId: string, songId: string) => void;
  /** Drops a song from every project. Called when the song itself is deleted. */
  removeSongEverywhere: (songId: string) => void;
  /** Marks which project the Studio is working in. Null on leaving one. */
  setActiveProject: (id: string | null) => void;
  /**
   * Fetches the projects through `studioProjectsApi.getProjects()`.
   *
   * The read that had no caller. Returns the empty set today, deliberately — see that
   * function's own comment on not fabricating projects the user never made — so what this
   * adds is the seam and the CONFIRMED-empty state, not new data.
   *
   * Never rejects; failures land in `load.error`.
   */
  loadProjects: () => Promise<void>;
  /** Replaces the set, e.g. from a future server fetch. */
  hydrate: (projects: StudioProject[]) => void;
  reset: () => void;
}

export const useStudioProjectsStore = create<StudioProjectsState>((set, get) => ({
  projects: [],
  seq: 0,
  activeProjectId: null,
  load: IDLE_LOAD,

  createProject(name, songIds, scope) {
    const seq = get().seq + 1;
    const project: StudioProject = {
      id: newId(),
      name,
      // Copied rather than referenced, as playlistsStore.createPlaylist does: the
      // caller's array is theirs, and a project holding a reference to it would change
      // when they next mutated it. De-duplicated on the way in, because "all songs"
      // over a library and a hand-picked id could otherwise arrive together.
      songIds: [...new Set(songIds)],
      scope,
      seq,
      createdAt: NOW_LABEL,
    };
    // Newest first, as the library and the playlists are.
    set((s) => ({ projects: [project, ...s.projects], seq }));
    void studioProjectsApi.createProject(name, project.songIds, scope);
    return project;
  },

  renameProject(id, name) {
    set((s) => ({ projects: s.projects.map((p) => (p.id === id ? { ...p, name } : p)) }));
    void studioProjectsApi.renameProject(id, name);
  },

  deleteProject(id) {
    set((s) => ({
      projects: s.projects.filter((p) => p.id !== id),
      // A deleted project cannot be the one being worked in. Left set, it would scope
      // the workspace's library strip to a project that is no longer in the list.
      activeProjectId: s.activeProjectId === id ? null : s.activeProjectId,
    }));
    void studioProjectsApi.deleteProject(id);
  },

  addSongToProject(projectId, songId) {
    const project = get().projects.find((p) => p.id === projectId);
    // Guarded rather than de-duplicated on read, for playlistsStore's reason: a
    // duplicate id would render the same track twice with the same React key in the
    // workspace's library strip, and removing one of them would remove both.
    if (!project || project.songIds.includes(songId)) return;
    const songIds = [...project.songIds, songId];
    set((s) => ({
      projects: s.projects.map((p) => (p.id === projectId ? { ...p, songIds } : p)),
    }));
    void studioProjectsApi.setProjectSongs(projectId, songIds);
  },

  removeSongFromProject(projectId, songId) {
    const project = get().projects.find((p) => p.id === projectId);
    if (!project) return;
    const songIds = project.songIds.filter((id) => id !== songId);
    set((s) => ({
      projects: s.projects.map((p) => (p.id === projectId ? { ...p, songIds } : p)),
    }));
    void studioProjectsApi.setProjectSongs(projectId, songIds);
  },

  removeSongEverywhere(songId) {
    const affected = get().projects.filter((p) => p.songIds.includes(songId));
    if (affected.length === 0) return;
    set((s) => ({
      projects: s.projects.map((p) =>
        p.songIds.includes(songId) ? { ...p, songIds: p.songIds.filter((id) => id !== songId) } : p
      ),
    }));
    // One call per affected project, because the endpoint is scoped to a project. A real
    // backend deleting the song would cascade this itself — noted rather than assumed,
    // since a cascade that does not happen leaves rows pointing at a deleted song. The
    // same note playlistsStore.removeSongEverywhere carries.
    for (const p of affected) {
      void studioProjectsApi.setProjectSongs(
        p.id,
        p.songIds.filter((id) => id !== songId)
      );
    }
  },

  setActiveProject(activeProjectId) {
    set({ activeProjectId });
  },

  async loadProjects() {
    set({ load: LOADING_LOAD });
    try {
      const projects = await studioProjectsApi.getProjects();
      // Through `hydrate`, which advances `seq` past the fetched set. See
      // songsStore.loadSongs for what a bypassed `seq` costs.
      get().hydrate(projects);
      set({ load: READY_LOAD });
    } catch (err) {
      /*
        `activeProjectId` IS NOT CLEARED on a failure, and that is worth stating: a failed
        refresh does not mean the project the user is working in has gone away. Clearing it
        would drop the workspace back to the whole library mid-session because a request
        timed out.
      */
      set({ load: failedLoad(err) });
    }
  },

  hydrate(projects) {
    set({ projects, seq: projects.length });
  },

  reset() {
    set({ projects: [], seq: 0, activeProjectId: null, load: IDLE_LOAD });
  },
}));

/** Looks up a project by id. */
export const selectProjectById = (
  projects: StudioProject[],
  id: string | null
): StudioProject | null => (id ? projects.find((p) => p.id === id) : null) ?? null;

/**
 * Resolves a project's ids into songs, in the order they were added.
 *
 * Unresolvable ids are dropped rather than rendered as a blank entry, for the reason
 * playlistSongs drops them: deleting a song already removes it from every project
 * (hooks/useSongs.deleteSong), so this is the second line of defence against a stale id
 * arriving from a future server or left behind by a failed write.
 */
export function projectSongs(project: StudioProject, songs: Song[]): Song[] {
  return project.songIds
    .map((id) => songs.find((s) => s.id === id))
    .filter((s): s is Song => s !== undefined);
}

/**
 * The one-line summary under a project's name: "3 tracks · picked one to start".
 *
 * Counts only songs that RESOLVE, for the reason projectSongs drops the others — a total
 * that included a deleted track would not match the list underneath it. No duration,
 * unlike a playlist's summary: a project is not something you play through, so a combined
 * running time would describe a thing it does not do.
 */
export function projectSummary(project: StudioProject, songs: Song[]): string {
  const tracks = projectSongs(project, songs);
  const count = `${tracks.length} ${tracks.length === 1 ? 'track' : 'tracks'}`;
  // The scope is stated because it is what the user chose, and because "1 track" reached
  // by "all songs" over a one-song library means something different from picking it.
  return `${count} · ${project.scope === 'all' ? 'whole library' : 'one track'}`;
}
